/**
 * CreatorStudio — Architecture 3.1 专家运营中心
 *
 * 全屏独立页面，双 Tab 结构：
 *   Tab A: 专家资源库 — 浏览、安装、卸载律所 AI 专家
 *   Tab B: 专属配置台 — 创建属于你自己的 AI 专家
 *
 * 遵循 Institutional Minimalist 设计语言，术语已降级为律所用语。
 */

import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, Users, Download, Trash2, Sparkles, Loader2, ArrowLeft, Plus } from 'lucide-react';
import { WorkflowApiService, MarketplaceAgent, InstalledAgent } from '../../services/workflowApi';
import ExpertSetupDesk from './ExpertSetupDesk';

// 向后兼容旧类型名
type MarketplaceSkill = MarketplaceAgent;
type InstalledSkill = InstalledAgent;

interface CreatorStudioProps {
    isOpen: boolean;
    onClose: () => void;
    currentLanguage: string;
    installedSkills: InstalledSkill[];
    onInstallChange: () => void;
}

type TabId = 'library' | 'create';

const CreatorStudio: React.FC<CreatorStudioProps> = ({
    isOpen,
    onClose,
    currentLanguage,
    installedSkills,
    onInstallChange,
}) => {
    const [activeTab, setActiveTab] = useState<TabId>('library');
    const [marketplaceItems, setMarketplaceItems] = useState<MarketplaceSkill[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [installingIds, setInstallingIds] = useState<Set<string>>(new Set());
    const [uninstallingIds, setUninstallingIds] = useState<Set<string>>(new Set());
    const [error, setError] = useState<string | null>(null);

    // 加载 Marketplace 数据
    useEffect(() => {
        if (!isOpen) return;

        setIsLoading(true);
        setError(null);
        WorkflowApiService.getMarketplace()
            .then(response => {
                setMarketplaceItems(response.items || []);
            })
            .catch(err => {
                console.error('[CreatorStudio] Failed to load marketplace:', err);
                setError('加载专家资源库失败，请稍后重试。');
            })
            .finally(() => setIsLoading(false));
    }, [isOpen]);

    // 已安装的 ID 集合
    const installedIdSet = useMemo(() => {
        return new Set(installedSkills.map(s => s.id));
    }, [installedSkills]);

    // 搜索过滤
    const filteredItems = useMemo(() => {
        if (!searchQuery.trim()) return marketplaceItems;
        const q = searchQuery.toLowerCase();
        return marketplaceItems.filter(
            item => item.name.toLowerCase().includes(q) || (item.description || '').toLowerCase().includes(q)
        );
    }, [searchQuery, marketplaceItems]);

    // 安装
    const handleInstall = async (agentId: string) => {
        setInstallingIds(prev => new Set(prev).add(agentId));
        try {
            await WorkflowApiService.install(agentId);
            onInstallChange();
        } catch (err) {
            console.error('[CreatorStudio] Install failed:', err);
        } finally {
            setInstallingIds(prev => {
                const next = new Set(prev);
                next.delete(agentId);
                return next;
            });
        }
    };

    // 卸载
    const handleUninstall = async (agentId: string) => {
        setUninstallingIds(prev => new Set(prev).add(agentId));
        try {
            await WorkflowApiService.uninstall(agentId);
            onInstallChange();
        } catch (err) {
            console.error('[CreatorStudio] Uninstall failed:', err);
        } finally {
            setUninstallingIds(prev => {
                const next = new Set(prev);
                next.delete(agentId);
                return next;
            });
        }
    };

    // 定价标签
    const getPricingLabel = (model: string, price: number) => {
        switch (model) {
            case 'free': return { text: '免费', color: 'bg-green-50 text-green-700 border-green-200' };
            case 'subscription': return { text: `¥${price}/月`, color: 'bg-amber-50 text-amber-700 border-amber-200' };
            case 'pay_per_use': return { text: `¥${price}/次`, color: 'bg-blue-50 text-blue-700 border-blue-200' };
            default: return { text: '免费', color: 'bg-green-50 text-green-700 border-green-200' };
        }
    };

    // 专家保存后刷新
    const handleAgentSaved = () => {
        onInstallChange();
        // 留在配置台，不切回大厅
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
            {/* ═══ Header ═══ */}
            <div className="border-b border-gray-200 bg-white">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="返回"
                        >
                            <ArrowLeft size={20} className="text-gray-600" />
                        </button>
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                <Users size={22} className="text-purple-500" />
                                专家运营中心
                            </h1>
                            <p className="text-sm text-gray-500 mt-0.5">
                                浏览、安装和定制您的 AI 法务专家团队
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                {/* ═══ Tab 导航 ═══ */}
                <div className="max-w-6xl mx-auto px-6">
                    <div className="flex gap-1">
                        <button
                            onClick={() => setActiveTab('library')}
                            className={`px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all border-b-2 ${
                                activeTab === 'library'
                                    ? 'text-purple-700 border-purple-500 bg-purple-50/50'
                                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <span className="flex items-center gap-2">
                                <Users size={14} />
                                专家资源库
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('create')}
                            className={`px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all border-b-2 ${
                                activeTab === 'create'
                                    ? 'text-purple-700 border-purple-500 bg-purple-50/50'
                                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <span className="flex items-center gap-2">
                                <Plus size={14} />
                                创建新专家
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            {/* ═══ Content ═══ */}
            <div className="flex-1 overflow-hidden">
                {activeTab === 'library' ? (
                    /* ═══ Tab A: 专家资源库 ═══ */
                    <div className="h-full overflow-y-auto">
                        <div className="max-w-6xl mx-auto px-6 py-6">
                            {/* 搜索框 */}
                            <div className="relative mb-6">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder="搜索专家..."
                                    className="pl-10 pr-4 py-2.5 w-full max-w-md border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all"
                                />
                            </div>

                            {/* 已安装区域 */}
                            {installedSkills.length > 0 && (
                                <div className="mb-8">
                                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                                        已聘用的专家 ({installedSkills.length})
                                    </h2>
                                    <div className="flex gap-3 overflow-x-auto pb-2">
                                        {installedSkills.map(skill => (
                                            <div
                                                key={skill.id}
                                                className="flex-shrink-0 flex items-center gap-3 px-4 py-3 bg-purple-50 border border-purple-200 rounded-xl min-w-[200px]"
                                            >
                                                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                                    <Users size={16} className="text-purple-500" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-gray-800 truncate">{skill.name}</div>
                                                    <div className="text-xs text-gray-500">
                                                        {skill.source === 'owned' ? '自建' : '已订阅'}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Banner */}
                            <div className="bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 rounded-2xl p-8 mb-8 border border-purple-100">
                                <div className="flex items-center gap-2 mb-2">
                                    <Sparkles size={20} className="text-purple-500" />
                                    <h2 className="text-lg font-semibold text-gray-800">专家集市</h2>
                                </div>
                                <p className="text-sm text-gray-600 max-w-xl">
                                    发现由社区和我们的团队精心打造的 AI 法务专家。
                                    安装专家即可解锁专属工作流——涵盖法律检索、文书起草、案件分析等全方位的律所业务。
                                </p>
                            </div>

                            {/* Loading */}
                            {isLoading && (
                                <div className="flex items-center justify-center py-20">
                                    <Loader2 size={32} className="text-purple-400 animate-spin" />
                                </div>
                            )}

                            {/* Error */}
                            {error && (
                                <div className="text-center py-20">
                                    <p className="text-gray-400 text-sm">{error}</p>
                                </div>
                            )}

                            {/* Grid */}
                            {!isLoading && !error && (
                                <>
                                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                                        全部专家 ({filteredItems.length})
                                    </h2>

                                    {filteredItems.length === 0 ? (
                                        <div className="text-center py-20">
                                            <Users size={48} className="mx-auto text-gray-200 mb-4" />
                                            <p className="text-gray-400 text-sm">
                                                {searchQuery ? '未找到匹配的专家' : '暂无已发布的专家'}
                                            </p>
                                            <button
                                                onClick={() => setActiveTab('create')}
                                                className="mt-4 text-sm text-purple-600 hover:text-purple-700 underline"
                                            >
                                                → 创建您的第一位 AI 专家
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {filteredItems.map(item => {
                                                const isInstalled = installedIdSet.has(item.id);
                                                const isInstalling = installingIds.has(item.id);
                                                const isUninstalling = uninstallingIds.has(item.id);
                                                const pricing = getPricingLabel(item.pricing_model, item.price);

                                                return (
                                                    <div
                                                        key={item.id}
                                                        className="border border-gray-200 rounded-xl p-5 hover:border-purple-200 hover:shadow-sm transition-all bg-white"
                                                    >
                                                        <div className="flex items-start justify-between mb-3">
                                                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                                                                <Users size={20} className="text-purple-400" />
                                                            </div>
                                                            <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${pricing.color}`}>
                                                                {pricing.text}
                                                            </span>
                                                        </div>

                                                        <h3 className="text-sm font-semibold text-gray-800 mb-1 truncate">
                                                            {item.name}
                                                        </h3>
                                                        <p className="text-xs text-gray-500 mb-4 line-clamp-2 min-h-[2rem]">
                                                            {item.description || '暂无简介'}
                                                        </p>

                                                        <div className="flex items-center justify-between">
                                                            <span className="text-xs text-gray-400">
                                                                v{item.version}
                                                            </span>

                                                            {isInstalled ? (
                                                                <button
                                                                    onClick={() => handleUninstall(item.id)}
                                                                    disabled={isUninstalling}
                                                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 transition-colors disabled:opacity-50"
                                                                >
                                                                    {isUninstalling ? (
                                                                        <Loader2 size={12} className="animate-spin" />
                                                                    ) : (
                                                                        <Trash2 size={12} />
                                                                    )}
                                                                    解聘
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    onClick={() => handleInstall(item.id)}
                                                                    disabled={isInstalling}
                                                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors disabled:opacity-50"
                                                                >
                                                                    {isInstalling ? (
                                                                        <Loader2 size={12} className="animate-spin" />
                                                                    ) : (
                                                                        <Download size={12} />
                                                                    )}
                                                                    聘用
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    /* ═══ Tab B: 创建新专家 ═══ */
                    <div className="h-full p-6">
                        <div className="max-w-6xl mx-auto h-full">
                            <ExpertSetupDesk
                                currentLanguage={currentLanguage}
                                onSaved={handleAgentSaved}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CreatorStudio;
